import React, { useEffect, useRef, useState } from 'react';
import { StyleSheet, TouchableOpacity, View, Platform } from 'react-native';
import { useSelector } from 'react-redux';
import colors from '../../../config/colors';
import { convertDateToLocal } from '../../../utils/convertDateToLocal.utils';
import Typography from '../../../components/shared/Typography/Typography.component';
import I18n from '../../../components/shared/i18n/I18n.component';
import { FontAwesome } from '@expo/vector-icons';
import useDevicePlatform, {
	DeviceType,
} from '../../../hooks/useDevicePlatform.hook';
import {
	HomePageReportContentElType,
	HomePageReportContentKeyType,
} from '../HomePage.container';
import { stringDataTransform } from './WageDataSummary.utils';
import { getDefaultDisplayWageMode } from '../../../store/selectors/config.selectors';
import {
	EventName,
	logEvent,
} from '../../../services/analyticsAndLogs.service';

type WageSummaryCardProps = {
	reportDateYear: string;
	reportContentData: HomePageReportContentElType;
};

const wageDataSummaryContent = [
	{ name: 'total', label: 'סה״כ תשלומים', style: 'normal' },
	{ name: 'deductions', label: 'ניכויים לרשויות וקופ״ג', style: 'normal' },
	{ name: 'otherDeductions', label: 'ניכויים אחרים', style: 'normal' },
	{ name: 'paymentNet', label: 'נטו לתשלום', style: 'bold' },
	{ name: 'vacationBalance', label: 'יתרת חופשה', style: 'normal' },
];

const mainWageItems = ['total', 'deductions', 'otherDeductions', 'paymentNet'];

const WageSummaryCard = ({
	reportDateYear,
	reportContentData,
}: WageSummaryCardProps) => {
	const hideWageMode = useSelector(getDefaultDisplayWageMode);
	const [showDetails, setShowDetails] = useState(hideWageMode || false);
	const isMobileRef = useRef<boolean>(useDevicePlatform() !== DeviceType.WEB);
	//const textFontSizeRef = useRef<FontSize>(isMobileRef.current ? 16 : 18);

	const hebrewMonths = [
		'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
		'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר',
	];
	const localDateTime = reportDateYear
		? (() => {
				const d = convertDateToLocal(reportDateYear);
				return `${hebrewMonths[d.month()]} ${d.year()}`;
		  })()
		: '';

	const valueDisplayHandler = (value: HomePageReportContentKeyType) => {
		const res = stringDataTransform(value, reportContentData);
		return res ? res : <Typography size={10}>---- ----</Typography>;
	};

	useEffect(() => {
		if (hideWageMode) {
			setShowDetails(true);
		}
	}, [hideWageMode]);

	return (
		<View style={styles.card}>
			<View style={styles.titleContainer}>
				{!hideWageMode ? (
					<TouchableOpacity
						style={styles.chevronButton}
						onPress={() => {
							if (!showDetails) {
								logEvent({
									eventName:
										EventName.reportWageDataSummaryShowButton,
									data: {},
								});
							}
							setShowDetails(prev => !prev);
						}}
					>
						<FontAwesome
							name={showDetails ? 'chevron-up' : 'chevron-down'}
							size={16}
							color={colors.primary}
						/>
					</TouchableOpacity>
				) : (
					<View />
				)}
				<View style={styles.titleRight}>
					<Typography
						size={14}
						weight='400'
						color='greyText'
						style={styles.dateText}
					>
						{localDateTime}
					</Typography>
					<I18n size={14} font='medium' color='primaryDarkBlue'>
						reportView.wageDataSummary.title
					</I18n>
				</View>
			</View>

			{showDetails && (
				<View style={[styles.contentContainer, isMobileRef.current && styles.contentContainerMobile]}>
					{wageDataSummaryContent
						.filter(el => mainWageItems.includes(el.name))
						.map((el, index, arr) => (
							<React.Fragment key={el.name}>
								<View style={[styles.contentColumn, isMobileRef.current && styles.contentColumnMobile]}>
									<Typography
										size={12}
										font='medium'
										color='greyText'
										style={styles.columnLabel}
									>
										{el.label}
									</Typography>
									<Typography
										size={20}
										weight={'500'}
										color='primaryDarkBlue'
									>
										₪
										{reportContentData &&
											valueDisplayHandler(
												el.name as HomePageReportContentKeyType,
											)}
									</Typography>
								</View>
							{!isMobileRef.current && index < arr.length - 1 && (
									<View style={styles.verticalDivider} />
								)}
							</React.Fragment>
						))}
				</View>
			)}
			{showDetails && reportContentData && (
				<>
					{!isMobileRef.current && <View style={styles.dividerMargined} />}
					<View style={[
						styles.vacationRow,
						isMobileRef.current && styles.vacationRowMobile,
					]}>
						{isMobileRef.current ? (
							<>
								<Typography size={12} weight='bold' color='greyText' style={styles.columnLabel}>
									יתרת חופשה
								</Typography>
								<Typography size={20} weight={'500'} color='primaryDarkBlue'>
									{valueDisplayHandler(
										'vacationBalance' as HomePageReportContentKeyType,
									)}
								</Typography>
							</>
						) : (
							<>
								<View style={styles.contentRowData}>
									<Typography size={18} weight={'500'} color='primaryDarkBlue'>
										{valueDisplayHandler(
											'vacationBalance' as HomePageReportContentKeyType,
										)}
									</Typography>
								</View>
								<View style={styles.contentRowText}>
									<Typography size={18} weight='400' color='greyText'>
										יתרת חופשה
									</Typography>
								</View>
							</>
						)}
					</View>
				</>
			)}
		</View>
	);
};

const styles = StyleSheet.create({
	card: {
		width: '100%',
		backgroundColor: colors.white,
		borderRadius: 8,
		padding: 24,
		...Platform.select({
			web: { gap: 16, boxShadow: '2px 2px 16px 0 rgba(0, 0, 0, 0.04)' },
			ios: { shadowColor: '#000', shadowOffset: { width: 2, height: 2 }, shadowOpacity: 0.04, shadowRadius: 16 },
			android: { elevation: 2 },
		}),
	},
	titleContainer: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: 16,
	},
	titleRight: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	dateText: {
		marginEnd: 16,
	},
	chevronButton: {
		padding: 8,
	},
	divider: {
		height: 2,
		opacity: 0.2,
		backgroundColor: colors.darkGrey,
	},
	dividerMargined: {
		height: 2,
		opacity: 0.2,
		backgroundColor: colors.darkGrey,
		marginTop: 20,
		marginBottom: 16,
	},
	contentContainer: {
		//paddingTop: 20,
		flexDirection: 'row',
		justifyContent: 'space-between',
		...Platform.select({ web: { gap: 32 }, default: {} }),
	},
	contentContainerMobile: {
		flexDirection: 'column-reverse',
		justifyContent: 'flex-start',
	},
	contentColumn: {
		flex: 1,
		alignItems: 'flex-end',
	},
	contentColumnMobile: {
		flex: undefined,
		alignItems: 'flex-end',
		marginBottom: 16,
	},

	columnLabel: {
		letterSpacing: 0.8,
		marginBottom: 8,
	},
	verticalDivider: {
		width: 1,
		opacity: 0.2,
		backgroundColor: colors.darkGrey,
		alignSelf: 'stretch',
		//marginHorizontal: 32,
	},
	vacationRow: {
		flexDirection: 'row',
		justifyContent: 'flex-end',
		marginTop: 4,
	},
	vacationRowMobile: {
		flexDirection: 'column',
		alignItems: 'flex-end',
		marginTop: 0,
		marginBottom: 16,
	},
	contentRowText: {
		minWidth: 160,
	},
	contentRowData: {
		marginEnd: 16,
	},
});

export default WageSummaryCard;
